import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider, useSocket } from './context/SocketContext';
import ErrorBoundary from './components/ErrorBoundary';
import Map from './components/Map';
import MapScreen from './components/MapScreen';
import {
  HomeIcon, MapIcon, MeetsIcon, MessagesIcon, ProfileIcon, CloseIcon,
} from './components/icons';
import Chat from './components/Chat';
import CarMeets from './components/CarMeets';
import Login from './components/Login';
import Register from './components/Register';
import Feed from './components/Feed';
import Profile from './components/Profile';
import LocationPrompt from './components/LocationPrompt';
import LocationHelp from './components/LocationHelp';
import NavigationHud from './components/NavigationHud';
import useUserPosition from './lib/useUserPosition';
import fetchRoute from './lib/routing';
import { readLocationPermission } from './lib/platform';
import api, { errorMessage, asArray } from './api';
import './App.css';

const BADGE_POLL_MS = 15000;
// Kartfliken ritar egna lager ovanpa kartan; ovriga flikar ar
// helskarmsvyer. Namnet star har sa att en omdopning inte kan lamna kvar
// halften av villkoren — det hande, och da lag en svart helskarmsvy over
// kartan tills man tryckte pa fliken en gang till.
const OVERLAY_PANEL = 'map';
const EMPTY_BADGES = { unread_messages: 0, pending_friends: 0, new_meets: 0, unread_from: [], new_meets_list: [] };

function AppContent() {
  const { user, token, loading } = useAuth();
  const { connected } = useSocket();

  // Hem ar utgangslaget: floden ar det man oppnar appen for.
  const [panel, setPanel] = useState('feed');
  // 'prompt' = webblasaren har inte fatt fragan pa den har enheten an.
  const [browserPermission, setBrowserPermission] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [route, setRoute] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [navigating, setNavigating] = useState(false);

  // Kartvyns eget tillstand: kartinstansen, filtret och vald plats.
  const [mapInstance, setMapInstance] = useState(null);
  const [meets, setMeets] = useState([]);
  const [places, setPlaces] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [placesBusy, setPlacesBusy] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [pickMode, setPickMode] = useState(false);
  const [pickedCoords, setPickedCoords] = useState(null);
  const [badges, setBadges] = useState(EMPTY_BADGES);
  const [profileUserId, setProfileUserId] = useState(null);
  const [locationPromptDone, setLocationPromptDone] = useState(false);
  const [showLocationHelp, setShowLocationHelp] = useState(false);
  // Meddelandet man valt att stanga. Byter felet karaktar visas det igen,
  // sa att ett nytt problem inte doljs av att man tystat ett gammalt.
  const [dismissedError, setDismissedError] = useState('');

  // Positionen bevakas bara nar man ar inloggad. Annars utloser den
  // webblasarens platsdialog redan pa inloggningssidan, dar anvandaren
  // varken bett om det eller kan forsta vad den galler.
  // Bevakningen startar forst nar tillstandet ar givet. Startar den innan
  // dess tystar Chrome pa Android fragan och svarar nej, utan att nagon
  // faktiskt sagt nej. Saknas Permissions API far ett forsok ge svaret.
  const canWatch = Boolean(user)
    && (browserPermission === 'granted' || browserPermission === 'unknown');

  const {
    position: userPosition,
    error: positionError,
    retry: retryLocation,
  } = useUserPosition(canWatch);


  // Webblasarens platstillstand pa den har enheten.
  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    let status = null;

    readLocationPermission().then(state => {
      if (!cancelled) setBrowserPermission(state);
    });

    // Andras det utanfor appen ska fragan forsvinna eller dyka upp.
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(result => {
          if (cancelled) return;
          status = result;
          result.onchange = () => setBrowserPermission(result.state);
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, [user]);

  const fetchBadges = useCallback(() => {
    if (!token) return;
    api.get('/notifications')
      .then(res => setBadges(res.data))
      .catch(() => { /* notiser är inte kritiska */ });
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    api.get('/meets', { params: { when: 'upcoming' } })
      .then(res => { if (!cancelled) setMeets(asArray(res.data)); })
      .catch(() => { /* kartan fungerar utan traffar */ });
    return () => { cancelled = true; };
  }, [token, panel]);

  // Platser i narheten nar ett filter ar valt.
  useEffect(() => {
    if (!token || filter === 'all' || !userPosition) {
      setPlaces([]);
      return undefined;
    }
    let cancelled = false;
    setPlacesBusy(true);
    api.get('/places', {
      params: { category: filter, lat: userPosition[0], lng: userPosition[1], radius: 4000 },
    })
      .then(res => { if (!cancelled) setPlaces(asArray(res.data)); })
      .catch(() => { if (!cancelled) setPlaces([]); })
      .finally(() => { if (!cancelled) setPlacesBusy(false); });
    return () => { cancelled = true; };
    // Positionen andras standigt; filtret ar det som ska utlosa en ny hamtning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filter]);

  useEffect(() => {
    if (!token) {
      setBadges(EMPTY_BADGES);
      return undefined;
    }
    fetchBadges();
    const interval = setInterval(fetchBadges, BADGE_POLL_MS);
    return () => clearInterval(interval);
  }, [token, fetchBadges]);

  if (loading) return <div className="loading">Laddar...</div>;

  if (!user) {
    return showRegister
      ? <Register onSwitch={() => setShowRegister(false)} />
      : <Login onSwitch={() => setShowRegister(true)} />;
  }

  /**
   * Valjer en flik. Att trycka pa den redan valda gor ingenting.
   *
   * Tidigare stangdes fliken av ett andra tryck, och kvar blev en karta
   * utan sokfalt, filter och knappar — ett lage man bara kunde hamna i av
   * misstag och inte kunde ta sig ur utan att trycka igen.
   */
  const togglePanel = (name) => setPanel(name);

  const openMeets = () => {
    togglePanel('meets');
    if (badges.new_meets > 0) {
      api.post('/notifications/meets-seen')
        .then(fetchBadges)
        .catch(() => {});
    }
  };

  const handleMapClick = (coords) => {
    if (!pickMode) return;
    setPickedCoords(coords);
    setPickMode(false);
    setPanel('meets');
  };

  /**
   * Fragan om platsdelning.
   *
   * Kontots flagga sager om man tagit stallning till att dela med vanner.
   * Webblasarens tillstand ar daremot knutet till enheten: den som sagt ja
   * pa sin telefon har inte sagt nagot alls i datorns webblasare. Darfor
   * styr tillstandet har, och kontoflaggan anvands bara for att slippa
   * fraga om samma sak igen pa en enhet dar det redan ar ordnat.
   */
  const DISMISS_KEY = 'locationPromptDismissed';
  const dismissedHere = (() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  })();

  const needsLocationPrompt = !locationPromptDone
    && !dismissedHere
    && browserPermission === 'prompt';

  const grantedNow = () => {
    setBrowserPermission('granted');
    retryLocation();
  };

  const finishLocationPrompt = () => {
    setLocationPromptDone(true);
    // Las om tillstandet: sa fort det ar givet ska bevakningen starta.
    readLocationPermission().then(setBrowserPermission);
    retryLocation();
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* privat läge */ }
  };

  /** Beraknar rutten till en vald plats och gar in i korvyn. */
  const startNavigation = async (place, speedKmh) => {
    if (!userPosition) {
      setRouteError('Vi vet inte var du är än — slå på platsåtkomst först.');
      setTimeout(() => setRouteError(''), 4000);
      return;
    }

    setRouteError('');
    try {
      const result = await fetchRoute({
        from: userPosition,
        to: [Number(place.lat), Number(place.lng)],
        speedKmh,
      });

      if (!result) {
        setRouteError('Ingen rutt hittades dit.');
        setTimeout(() => setRouteError(''), 4000);
        return;
      }

      setRoute(result.coords);
      setRouteInfo(result.info);
      setSelectedPlace(null);
      setNavigating(true);
      setPanel(null);
    } catch (err) {
      setRouteError(errorMessage(err, 'Kunde inte beräkna rutt.'));
      setTimeout(() => setRouteError(''), 5000);
    }
  };

  const stopNavigating = () => {
    setNavigating(false);
    setRoute(null);
    setRouteInfo(null);
  };

  const chatBadge = (badges.unread_messages || 0) + (badges.pending_friends || 0);

  const TITLES = {
    feed: 'Hem',
    chat: 'Meddelanden',
    meets: 'Träffar',
    profile: profileUserId ? 'Profil' : 'Din profil',
  };

  // Krysset lamnar vyn till kartan, inte till ingenting.
  const closePanel = () => { setPanel(OVERLAY_PANEL); setProfileUserId(null); };

  const tab = (name, IconComponent, label, badge, onClick) => (
    <button
      className={panel === name ? 'tabbar__tab is-active' : 'tabbar__tab'}
      onClick={onClick || (() => togglePanel(name))}
      aria-current={panel === name ? 'page' : undefined}
    >
      <span className="tabbar__icon">
        <IconComponent size={25} />
        {badge > 0 && <span className="badge">{badge}</span>}
      </span>
      <span className="tabbar__label">{label}</span>
    </button>
  );

  return (
    <div className="app">
      {needsLocationPrompt && (
        <LocationPrompt onDone={finishLocationPrompt} />
      )}

      {showLocationHelp && (
        <LocationHelp
          onClose={() => setShowLocationHelp(false)}
          onRetry={grantedNow}
          siteAllowed={browserPermission === 'granted'}
        />
      )}

      <Map
        route={route}
        onMapClick={handleMapClick}
        pickMode={pickMode}
        userPosition={userPosition}
        navigating={navigating}
        meets={meets}
        places={panel === OVERLAY_PANEL && !navigating ? places : []}
        selectedPlaceId={selectedPlace && selectedPlace.id}
        onSelectPlace={setSelectedPlace}
        onMapReady={setMapInstance}
      />

      {navigating && (
        <NavigationHud
          route={route}
          info={routeInfo}
          position={userPosition}
          onStop={stopNavigating}
        />
      )}

      {/* Varningar ligger som flytande remsor i stallet for att knuffa
          innehallet nedat. */}
      {!navigating && (
        <div className="toasts">
          {routeError && <div className="toast">{routeError}</div>}
          {!connected && <div className="toast">Ingen kontakt med servern — försöker återansluta...</div>}
          {positionError && positionError !== dismissedError && (
            <div className="toast toast--action">
              <span>{positionError}</span>
              {/* Nekas positionen medan sidan ar tillaten sitter sparren i
                  systemet, och da hjalper inget nytt forsok. */}
              {browserPermission === 'denied' || /Platstjänster/.test(positionError) ? (
                <button onClick={() => setShowLocationHelp(true)}>Så här fixar du det</button>
              ) : (
                <button onClick={retryLocation}>Försök igen</button>
              )}
              <button
                className="toast__close"
                onClick={() => setDismissedError(positionError)}
                aria-label="Stäng meddelandet"
              >
                <CloseIcon size={15} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Kartvyn ligger som lager ovanpa kartan, inte som en egen sida. */}
      {panel === OVERLAY_PANEL && !navigating && (
        <MapScreen
          map={mapInstance}
          userPosition={userPosition}
          meets={meets}
          places={places}
          filter={filter}
          onFilter={setFilter}
          selectedPlace={selectedPlace}
          onSelectPlace={setSelectedPlace}
          onNavigate={startNavigation}
          busy={placesBusy}
          onLocationGranted={grantedNow}
        />
      )}

      {panel && panel !== OVERLAY_PANEL && !navigating && (
        <section className="screen">
          <header className="screen__top">
            <h1 className="screen__title">{TITLES[panel]}</h1>
            <button className="screen__close" onClick={closePanel} aria-label="Stäng">
              <CloseIcon size={19} />
            </button>
          </header>

          <div className="screen__content">
            {panel === 'feed' && (
              <Feed onViewProfile={(id) => { setProfileUserId(id); setPanel('profile'); }} />
            )}

            {panel === 'chat' && (
              <Chat
                onViewProfile={(id) => { setProfileUserId(id); setPanel('profile'); }}
                onMessagesRead={fetchBadges}
                onOpenMeet={() => { setPanel('meets'); }}
              />
            )}

            {panel === 'meets' && (
              <CarMeets
                onPickMode={() => { setPickMode(true); setPanel(null); }}
                pickedCoords={pickedCoords}
                clearPickedCoords={() => setPickedCoords(null)}
                newMeetsList={badges.new_meets_list || []}
                onViewProfile={(id) => { setProfileUserId(id); setPanel('profile'); }}
              />
            )}

            {panel === 'profile' && (
              <Profile
                userId={profileUserId}
                onBack={profileUserId ? () => { setProfileUserId(null); setPanel('chat'); } : null}
                onLocationGranted={grantedNow}
                onViewProfile={(id) => setProfileUserId(id)}
              />
            )}
          </div>
        </section>
      )}

      {/* Ordningen ar densamma overallt: Hem, Karta, Traffar, Meddelanden, Profil. */}
      <nav className="tabbar">
        {tab('feed', HomeIcon, 'Hem')}
        {tab(OVERLAY_PANEL, MapIcon, 'Karta')}
        {tab('meets', MeetsIcon, 'Träffar', badges.new_meets, openMeets)}
        {tab('chat', MessagesIcon, 'Meddelanden', chatBadge, () => { togglePanel('chat'); fetchBadges(); })}
        {tab('profile', ProfileIcon, 'Profil', 0, () => { setProfileUserId(null); togglePanel('profile'); })}
      </nav>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <AppContent />
        </SocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
